'use client'

import { SSEContextProvider, useSSEContext } from '../contexts/sse-context'
import { useSessionSSE } from '../hooks/useDuplicateSessionIdentifier'
import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux-hook'
import { SSE_MESSAGE_TYPES_ENUM } from 'app/app/common/sse/sse-utils'
import { useSession } from 'next-auth/react'
import { AppSession } from 'app/app/api/auth/auth'

function SSEGlobalHandlers() {
  const { subscribe, unsubscribe } = useSSEContext()
  const dispatch = useAppDispatch()
  const { status: sessionStatus, data: session } = useSession()
  const { data: isAppInitialized } = useAppSelector(state => state.appInitialized) || { data: false }
  const appSession = session as AppSession | undefined
  const { reclaimSession } = useSessionSSE()

  useEffect(() => {
    const handleVersion = (message: any) => {
      if (message.type === SSE_MESSAGE_TYPES_ENUM.VERSION) {
        const currentVersion = process.env.NEXT_PUBLIC_APP_VERSION
        if (currentVersion && message.version !== currentVersion) {
          // This will be replaced with a proper notification to the user in the future
          console.log(`New version detected: ${message.version} (current: ${currentVersion}). Reloading...`)
          setTimeout(() => window.location.reload(), 5000)
        }
      }
    }

    const handleUpgradeMode = (message: any) => {
      if (message.type === SSE_MESSAGE_TYPES_ENUM.UPGRADE_MODE_ON) {
        // This will be replaced with a proper notification to the user in the future
        console.log(`Upgrade mode activated...`)
      }
    }

    subscribe(SSE_MESSAGE_TYPES_ENUM.VERSION, handleVersion)
    subscribe(SSE_MESSAGE_TYPES_ENUM.UPGRADE_MODE_ON, handleUpgradeMode)

    return () => {
      unsubscribe(SSE_MESSAGE_TYPES_ENUM.VERSION, handleVersion)
      unsubscribe(SSE_MESSAGE_TYPES_ENUM.UPGRADE_MODE_ON, handleUpgradeMode)
    }
  }, [subscribe, unsubscribe, dispatch])

  useEffect(() => {
    if (
      sessionStatus !== 'authenticated' ||
      !isAppInitialized ||
      !appSession?.profile?.user_id ||
      !appSession?.sessionId
    ) {
      return
    }
    const {
      profile: { user_id: userId }
    } = appSession
    const sessionId = appSession.sessionId
    reclaimSession(userId, sessionId)
  }, [appSession, isAppInitialized, sessionStatus, reclaimSession])

  return null
}

export function SSEProvider({ children }: { children: React.ReactNode }) {
  return (
    <SSEContextProvider
      options={{
        autoConnect: true
      }}
    >
      <SSEGlobalHandlers />
      {children}
    </SSEContextProvider>
  )
}
